import React, { useEffect, useState } from "react";
import "./AddCompany.css";
import { useNavigate } from "react-router-dom";
import ApiManager from "../../ApiManager/ApiManager";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";


const forcedFuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid", "CNG", "LPG", "None"];

const AddCompany = () => {
  const [taxTypes, setTaxTypes] = useState([]);
  const navigate = useNavigate(); // Get the navigate function
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  useEffect(() => {
    fetchTaxTypes();
  }, []);

  const fetchTaxTypes = async () => {
    try {
      await withLoading('fetchTaxTypes', async (onProgress) => {
        onProgress(20, 'Fetching tax types...');
        const params = new URLSearchParams({
          page_size: 1000,
          page: 1,
        });
        const response = await ApiManager.getAllTaxes(params);
        if (!response.ok) throw new Error("Network response was not ok");
        onProgress(60, 'Processing tax data...');
        const json = await response.json();
        console.log("Tax types", json);
        setTaxTypes(json);
        onProgress(100, 'Tax types loaded');
      });
    } catch (error) {
      console.log("Error fetching tax types", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const companyData = Object.fromEntries(formData.entries());

    const payload = {
      username: companyData.username,
      name: companyData.name,
      address: companyData.address,
      state: companyData.state,
      zip: companyData.zip,
      country: companyData.country,
      email: companyData.email,
      phoneNumber: companyData.phoneNumber,
      altPhoneNumber: companyData.altPhoneNumber,
      altEmail: companyData.altEmail,
      remarks: companyData.remarks,
      financeInfo: {
        billingName: companyData.billingName,
        billingAddress: companyData.billingAddress,
        billingState: companyData.billingState,
        panNumber: companyData.panNumber,
        gstNumber: companyData.gstNumber,
        gstType: companyData.gstType,
        tdsPercentage: parseFloat(companyData.tdsPercentage),
        forcedFuelType: companyData.forcedFuelType,
      },
      mappedTaxTypes:
        (taxTypes && taxTypes.length > 0
          ? taxTypes.filter((taxType) =>
            companyData[`taxType_${taxType.id}`] === "on"
          ).map((taxType) => ({
            id: taxType.id,
            name: taxType.name,
            description: taxType.description,
            percentage: taxType.percentage,
          }))
          : []),
    };

    try {
      await withLoading('addCompany', async (onProgress) => {
        onProgress(20, 'Preparing company data...');
        console.log("Payload:", payload);
        
        onProgress(50, 'Creating company...');
        const response = await ApiManager.addCompany(payload);
        console.log("Response:", response);
        
        if (!response.ok) throw new Error(await response.text());
        
        onProgress(80, 'Company created successfully');
        navigate('/login/companies');
        onProgress(100, 'Redirecting...');
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error.message || "Failed to add company");
    }
  };

  if (isLoading('fetchTaxTypes')) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: 'var(--font-size-xs)'
      }}>
        <LoadingSpinner 
          message={getMessage('fetchTaxTypes')}
          progress={getProgress('fetchTaxTypes')}
          showProgress={true}
          fullscreen={true}
        />
      </div>
    );
  }

  return (
    <div className="addCompany">
      <div>
        <h3>Add Company</h3>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Company Details Section */}
        <fieldset className="fieldset">
          <legend>Company Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                placeholder="Enter username"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Company Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter company name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                placeholder="Enter address"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>State *</label>
              <input
                type="text"
                name="state"
                placeholder="Enter state"
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Zip *</label>
              <input
                type="text"
                name="zip"
                placeholder="Enter zip code"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Country *</label>
              <input
                type="text"
                name="country"
                placeholder="Enter country"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                placeholder="Enter company email"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                placeholder="Enter phone number"
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>Alternate Phone Number</label>
              <input
                type="tel"
                name="altPhoneNumber"
                placeholder="Enter alternate phone number"
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Alternate Email</label>
              <input
                type="email"
                name="altEmail"
                placeholder="Enter alternate email"
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Remarks</label>
              <textarea
                name="remarks"
                placeholder="Enter remarks"
                className="input"
              ></textarea>
            </div>
          </div>
        </fieldset>

        {/* Finance Information Section */}
        <fieldset className="fieldset">
          <legend>Finance Information</legend>
          <div className="row">
            <div className="formGroup">
              <label>Billing Name *</label>
              <input
                type="text"
                name="billingName"
                placeholder="Enter billing name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Billing Address *</label>
              <input
                type="text"
                name="billingAddress"
                placeholder="Enter billing address"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Billing State *</label>
              <input
                type="text"
                name="billingState"
                placeholder="Enter billing state"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>PAN Number *</label>
              <input
                type="text"
                name="panNumber"
                placeholder="Enter PAN number"
                required
                className="input"
              />
            </div>
          </div>
          <div className="row">
            <div className="formGroup">
              <label>GST Number *</label>
              <input
                type="text"
                name="gstNumber"
                placeholder="Enter GST number"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>GST Type *</label>
              <select name="gstType" required className="input">
                <option value="">Select GST Type</option>
                <option value="Regular">Regular</option>
                <option value="Composition">Composition</option>
              </select>
            </div>
            <div className="formGroup">
              <label>TDS Percentage *</label>
              <input
                type="number"
                step="0.1"
                name="tdsPercentage"
                placeholder="Enter TDS percentage"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Forced Fuel Type *</label>
              <select
                name="forcedFuelType"
                required
                className="input"
              >
                {forcedFuelTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Tax Types Section */}
        <fieldset className="fieldset">
          <legend>Tax Types</legend>
          {taxTypes && taxTypes.length > 0 ? (
            taxTypes.map((taxType) => (
              <div key={taxType.id} className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    name={`taxType_${taxType.id}`}
                  />
                  {taxType.name} ({taxType.percentage}%)
                </label>
              </div>
            ))
          ) : (
            <div className="formGroup">
              <p>No tax types available.</p>
            </div>
          )}
        </fieldset>

        {/* Submit Button */}
        <button 
          type="submit" 
          className="submitButton"
          disabled={isLoading('addCompany')}
        >
          {isLoading('addCompany') ? (
            <LoadingSpinner 
              size="xs"
              inline={true}
            />
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default AddCompany;
